import React, { ReactNode, useState } from 'react'
import { color, palette } from '../../theme'
import { observer } from 'mobx-react-lite'
// @ts-ignore
// import defaultCover from './default-topic-image.png'
import { animated, useSpring } from '@react-spring/web'
import { RecallWheel } from '../recall-wheel'
import { SvgIcon } from '../svg-icon'
import { Text } from '../text'
import { iconLibraryPlay, iconLibraryPlay_White, iconLibraryLock_Grey } from '../../assets/icons'
import { useTranslation } from 'react-i18next'
import {
  Image,
  ImageStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  Platform,
  TextStyle,
  Dimensions,
} from 'react-native'
import { Instance } from 'mobx-state-tree'
import { AssignedQuiz } from '@trivie/core/src/models/UserQuiz'
import { DateTime } from 'luxon'
import { useInterval } from '@trivie/core'
const defaultCover = require('./default-topic-image.png')

interface QuizCardLiteProps {
  image?: string
  subtitle?: string
  onClick: () => void
  onEdit?: () => void
  onExport?: () => void
  onShare?: () => void
  onDelete?: () => void
  style?: React.CSSProperties
  imageStyle?: ImageStyle
  containerStyle?: React.CSSProperties
  quiz: Instance<typeof AssignedQuiz>
  isMobile?: boolean
}

export const QuizCardLite = observer((props: QuizCardLiteProps) => {
  const {
    onEdit,
    onExport,
    onShare,
    onDelete,
    image,
    subtitle,
    onClick,
    style,
    imageStyle,
    containerStyle,
    quiz,
    isMobile,
    ...rest
  } = props

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const { t } = useTranslation()

  const trans: any = (x, y, s) => `scale(${s})`
  const [spr, set] = useSpring(() => ({
    xys: [0, 0, 1],
    config: { mass: 4, tension: 350, friction: 60 },
  }))
  const calc: any = (x, y) => [
    -(y - window.innerHeight / 2) / 20,
    (x - window.innerWidth / 2) / 20,
    1.07,
  ]

  const playIcon = quiz.quiz_status === 'In Progress' ? iconLibraryPlay_White : iconLibraryPlay
  const recallWheel_circleColour = 'rgba(27, 89, 248, 1)'
  const recallWheel_strokeColour = 'rgba(27, 89, 248, 0.2)'
  const recallWheel_iconColour = 'rgba(27, 89, 248, 0.8)'
  const recallWheelComp_circleColour = 'rgba(129, 52, 254, 1)'
  const recallWheelComp_strokeColour = 'rgba(129, 52, 254, 0.1)'
  const recallWheelComp_iconColour = 'rgba(129, 52, 254, 0.8)'

  // const progressValue = quiz.number_questions / 2 / quiz.number_questions //fixed to %50, need data like "answered_question"
  const progressValue = (quiz.best_score ? quiz.best_score : 0) / 100
  const subTitleTextColor = quiz.pack_color ? color.white : '#9098A3'
  const completed = (quiz.completed_attempts || 0) > 0
  const started = quiz.quiz_status === 'In Progress'

  const [unlockTime, setUnlockTime] = useState<any>(
    DateTime.fromISO(quiz.unlocks_at ?? '').toRelative(),
  )
  const [forceUnlock, setForceUnlock] = useState<any>(false)

  useInterval(() => {
    if (quiz.is_locked && !forceUnlock) {
      const t = DateTime.fromISO(quiz.unlocks_at ?? '').diffNow('seconds').seconds
      if (t / 60 <= 0) {
        setForceUnlock(true)
      }
      if (t / 60 < 1) {
        setUnlockTime(DateTime.fromISO(quiz.unlocks_at ?? '').toRelative())
      }
    }
  }, 1000)

  const renderSubtitle = () => {
    if (quiz.is_locked) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
              marginBottom: 2,
            }}
            tx="Topic.locked"
          />
          <Text variant="body2Medium" style={SUBTITLE} text={unlockTime} />
        </View>
      )
    } else if (completed) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
              marginBottom: 2,
            }}
            tx={'Common.complete'}
          />
          <Text
            variant="body2Medium"
            style={{ color: subTitleTextColor }}
            tx={'Certifications.bestScorePct'}
            txOptions={{ score: quiz.best_score }}
          />
        </View>
      )
    } else if (!started) {
      return (
        <View style={{ ...COLUMN, justifyContent: 'flex-end' }}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
              marginBottom: 2,
            }}
            tx="Topic.notStarted"
          />
        </View>
      )
    } else if (started) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
              marginBottom: 2,
            }}
            tx="Quiz.inProgress"
          />
          <Text
            variant="body2"
            style={{ color: subTitleTextColor }}
            tx="Topic.pctCompleted"
            txOptions={{
              value: Math.round(progressValue * 100).toFixed(0),
            }}
          />
        </View>
      )
    }
  }

  const renderIcon = () => {
    if (quiz.is_locked) {
      return (
        <View style={{ alignSelf: 'flex-end', height: 28, width: 28 }}>
          <SvgIcon src={iconLibraryLock_Grey} />
        </View>
      )
    } else if (completed) {
      return (
        <RecallWheel
          style={{ alignSelf: 'flex-end' }}
          variant="small"
          icon="check"
          value={progressValue}
          circleColour={recallWheelComp_circleColour}
          iconColour={recallWheelComp_iconColour}
          strokeColor={recallWheelComp_strokeColour}
        />
      )
    } else if (!started) {
      return <SvgIcon src={playIcon} size={24} />
    } else if (started) {
      return (
        <RecallWheel
          style={{ alignSelf: 'flex-end' }}
          variant="small"
          icon="play"
          iconColour={recallWheel_iconColour}
          value={progressValue}
          circleColour={recallWheel_circleColour}
          strokeColor={recallWheel_strokeColour}
        />
      )
    }
  }

  const renderContent = () => (
    <View style={CARD}>
      <TouchableOpacity style={TOPIC_CARD} onPress={onClick}>
        <View style={CARD_CONTAINER}>
          <View style={{ ...CARD_CONTENT, backgroundColor: quiz.pack_color ?? color.white }}>
            <View style={COLUMN}>
              <Image
                style={{ ...IMAGE, ...imageStyle }}
                resizeMode="cover"
                source={image ? { uri: image } : defaultCover}
              />
              <View>
                <Text
                  adjustsFontSizeToFit
                  // onTextLayout={onTextLayout}
                  numberOfLines={2}
                  variant="body1Emphasized"
                  text={quiz.title}
                  style={{
                    ...CARD_TEXT,
                    color: color.black,
                  }}
                />
              </View>
            </View>
            <View style={FOOTER}>
              <View style={FOOTER_CONTENT}>
                {renderSubtitle()}
                {renderIcon()}
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  )

  if (Platform.OS === 'web') {
    return (
      <animated.div
        onMouseEnter={({ clientX: x, clientY: y }: any) => set({ xys: calc(x, y) })}
        onMouseLeave={() => set({ xys: [0, 0, 1] })}
        style={{
          width: isMobile ? 155 : 170,
          height: 190,
          // @ts-ignore
          transform: spr.xys.interpolate(trans),
          ...containerStyle,
        }}
        {...rest}
      >
        {renderContent()}
      </animated.div>
    )
  }
  return (
    <View style={CARD}>
      <TouchableOpacity style={TOPIC_CARD} onPress={onClick}>
        <View style={CARD_CONTAINER}>
          <View style={{ ...CARD_CONTENT, backgroundColor: quiz.pack_color ?? color.white }}>
            <View style={COLUMN}>
              <Image
                style={{ ...IMAGE, ...imageStyle }}
                resizeMode="cover"
                source={image ? { uri: image } : defaultCover}
              />
              <View>
                <Text
                  adjustsFontSizeToFit
                  // onTextLayout={onTextLayout}
                  numberOfLines={2}
                  variant="body1Emphasized"
                  text={quiz.title}
                  style={{
                    ...CARD_TEXT,
                    color: color.black,
                  }}
                />
              </View>
            </View>
            <View style={FOOTER}>
              <View style={FOOTER_CONTENT}>
                {renderSubtitle()}
                {renderIcon()}
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  )
})

const CARD_CONTAINER: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
  borderRadius: 23,
  backgroundColor: color.white,
}
const CARD: ViewStyle = {
  flex: 1,
  maxWidth: Dimensions.get('screen').width / 2 - 26,
  borderRadius: 23,
}
const TOPIC_CARD: ViewStyle = {
  height: 200,
  elevation: 2,
  borderRadius: 23,
  flexDirection: 'column',
  marginBottom: 16,
  justifyContent: 'flex-start',
  borderWidth: 1,
  borderColor: '#EFF0F6',
}
const CARD_CONTENT: ViewStyle = {
  padding: 12,
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: 23,
}
const CARD_TEXT: TextStyle = { paddingHorizontal: 4, lineHeight: undefined }
const IMAGE: ImageStyle = { height: 87, width: '100%', marginBottom: 8, borderRadius: 12 }
const FOOTER: ViewStyle = {
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: 4,
}
const FOOTER_CONTENT: ViewStyle = {
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const COLUMN: ViewStyle = { flexDirection: 'column' }

const SUBTITLE: TextStyle = {
  color: palette.grey,
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: 9,
}
